import * as Sentry from '@sentry/react';
import {
  browserTracingIntegration,
  reactRouterV6BrowserTracingIntegration,
} from '@sentry/react';
import React, { useEffect } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { Slide, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { restHost } from './apiConfig';
import Engineers from './components/Customers/Engineers';
import CallStatsDashboard from './components/CallStats/CallStatsDashboard';
import EngineerOnlyRoute from './components/CallStats/EngineerOnlyRoute';
import CareTeamDetails from './components/Customers/components/CareTeamDetails';
import CreatePrimaryContact from './components/Customers/components/CreatePrimaryContact';
import CustomerDetail from './components/Customers/components/CustomerDetail';
import MedicationEventHistory from './components/Customers/components/MedicationEventHistory';
import CreateNewFacilityUser from './components/Customers/CreateNewFacilityUser';
import CreateNewOwner from './components/Customers/CreateNewOwner';
import Customers from './components/Customers/Customers';
import Owners from './components/Customers/Owners';
import DeviceDetail from './components/Devices/DeviceDetail';
import Devices from './components/Devices/Devices';
import Operations from './components/Operations/Operations';
import AddNewFacility from './components/Facility/AddNewFacility';
import Facilities from './components/Facility/Facilities';
import FacilityDetail from './components/Facility/FacilityDetail';
import MyFacilityDetail from './components/Facility/MyFacilityDetail';
import MyAccount from './components/MyAccount/MyAccount';
import Pharmacies from './components/Pharmacy/Pharmacies';
import PharmacyDetail from './components/Pharmacy/PharmacyDetail';
import Register from './components/Register';
import Reporting from './components/Reporting/Reporting';
import CreateNewSoftwareVersion from './components/SoftwareVersions/CreateNewSoftwareVersion';
import EditSoftwareVersion from './components/SoftwareVersions/EditSoftwareVersion';
import SoftwareDeployment from './components/SoftwareVersions/SoftwareVersionDeployment';
import Software from './components/SoftwareVersions/SoftwareVersions';
import './css/App.css';
import './css/toast.css';
import {
  getCookie,
  getDefaultUIModeForRole,
  getFacilityRoleById,
  getPrivilegeById,
} from './shared/utils';
import 'react-toastify/dist/ReactToastify.css';
import NavBarIndex from './components/Dashboard/NavBarIndex';
import DashboardIndex from './components/Dashboard/DashboardIndex';
import Login from './components/Auth/Login';
import StatusPage from './components/Status/StatusPage';
import ResetPassword from './components/Auth/ResetPassword';
import RegisterEmailVerify from './components/Auth/RegisterEmailVerify';
import HubOwners from './components/HubOwners/HubOwners';
import HubOwnerProfile from './components/HubOwners/HubOwnerProfile';
import CaregiversNew from './components/Caregivers/CaregiversNew';
import CaregiverProfile from './components/Caregivers/CaregiverProfile';
import HubIndex from './components/Hubs/HubIndex';
import HubProfile from './components/Hubs/HubProfile';
import { RootState } from './store';
import { useDispatch, useSelector } from 'react-redux';
import AccountSettings from './components/AccountSettings/AccountSettings';
import { setToggleUI } from './store/uiToggle/actionCreators';
import LoadingSpinner from './components/Shared/LoadingSpinner';
import CustomerCareUsers from './components/Customers/CustomerCareUsers';
import PharmacyTechUsers from './components/Customers/PharmacyTechUsers';
import { UserRoles } from './shared/constants';
import CustomerCareSoftwareView from './components/SoftwareVersions/CustomerCareSoftwareView';
import PatientSurvey from './components/PatientSurvey/PatientSurvey';
import Adherence from './components/Adherence/Adherence';
import Issues from './components/Issues/Issues';
import JanusSpike from './components/VideoCall/JanusSpike';
import FacilitiesOverview from './components/ModernFacility/FacilitiesOverview';
import FacilityDetails from './components/ModernFacility/FacilityDetails/FacilityDetails';
import PatientOverview from './components/Patients/PatientOverview';
import PatientDetails from './components/Patients/PatientDetails/PatientDetails';
import PharmacyPatientOverview from './components/PharmacyPatients/PharmacyPatientOverview';
import PharmacyPatientDetails from './components/PharmacyPatients/PharmacyPatientDetails';
import SessionManager from './components/Auth/SessionManager';
import DocumentProcessingMonitor from './components/Documents/DocumentProcessingMonitor';
import DocumentsPage from './pages/Documents';
import VideoCallModalV2 from './components/VideoCall/v2/VideoCallModalV2';
import IncomingCallBannerV2 from './components/VideoCall/v2/IncomingCallBannerV2';

Sentry.init({
  dsn:
    process.env.NODE_ENV === 'development'
      ? ''
      : 'https://207dc18ba022416c8b41fb9b4c2351ee@o926607.ingest.sentry.io/6375017',
  environment: process.env.REACT_APP_CUSTOM_NODE_ENV || process.env.NODE_ENV,
  integrations: [
    browserTracingIntegration({
      shouldCreateSpanForRequest: (url: string) => url.includes(restHost),
    }),
    reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: 1.0,
});

const stateSelector = (state: RootState) => {
  const { uiToggle, dashboardUser } = state;
  const { useNewUI } = uiToggle;
  const { dashboardUserInfo } = dashboardUser;
  return {
    useNewUI,
    dashboardUserInfo,
  };
};

const universalRoutes =
  process.env.NODE_ENV === 'development'
    ? ['/', '/myAccount', '/dev/janus-spike']
    : ['/', '/myAccount'];

// function to determine user role
const getUserRole = (dashboardUserInfo: any): UserRoles => {
  // Adding UNDEFINED role here because using UNKNOWN causes the page to redirect to /
  // whenever a page reload happens. This is because the dashboardUserInfo redux state
  // gets reset (i.e. undefined) and since isRestrictedRole includes UserRoles.UNKNOWN
  // it causes the page to automatically redirect to / before the dashboardUserInfo
  // redux state can be refreshed again
  if (!dashboardUserInfo) return UserRoles.UNDEFINED;

  return dashboardUserInfo.facility_role_id
    ? (getFacilityRoleById(dashboardUserInfo.facility_role_id) as UserRoles)
    : (getPrivilegeById(dashboardUserInfo?.privilege_id ?? 0) as UserRoles);
};

interface ProtectedRouteProps {
  element: React.ReactElement;
}
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ element }) => {
  const { useNewUI, dashboardUserInfo } = useSelector(stateSelector);
  const [isLoading, setLoading] = React.useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    // Function to initialize the UI state based on session storage or user role
    const initializeUIState = () => {
      // Check if the UI state is stored in session storage
      const currentRole = getUserRole(dashboardUserInfo);
      const storedUseNewUI = sessionStorage.getItem('useNewUI');

      if (storedUseNewUI !== null) {
        // If found, set the UI state from session storage
        dispatch(setToggleUI(storedUseNewUI === 'true'));
      } else {
        // If user role is not yet defined, we cannot determine the default UI mode yet.
        // We wait until dashboardUserInfo is populated.
        if (currentRole !== UserRoles.UNDEFINED) {
          console.log(
            `UI mode for role ${currentRole} set to ${getDefaultUIModeForRole(
              currentRole,
            )}`,
          );
          // If not found in session storage, determine the UI state based on user role
          dispatch(setToggleUI(getDefaultUIModeForRole(currentRole)));
        } else {
          console.log('User role is not yet defined');
        }
      }
      dispatch(setLoading(false));
    };
    // Call the above function to initialize the UI state
    initializeUIState();
  }, [dashboardUserInfo, dispatch]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const currentRole = getUserRole(dashboardUserInfo);
  const currentPath = window.location.pathname;

  // Restrict access to universalRoutes based on user roles
  const isRestrictedRole = [
    UserRoles.FACILITY_CAREGIVER,
    UserRoles.FACILITY_OWNER,
    UserRoles.FACILITY_NURSE,
    UserRoles.FACILITY_UNKNOWN,
    UserRoles.NONE,
    UserRoles.STANDARD,
  ].includes(currentRole);

  if (isRestrictedRole && !universalRoutes.includes(currentPath)) {
    return <Navigate to="/" replace />;
  }

  // Pharmacy techs (privilege_id === 4) are confined to universal routes plus
  // their dedicated pharmacy view; everything else redirects home.
  const isPharmacyTech = currentRole === UserRoles.PHARMACY_TECH;
  const pharmacyTechAllowed =
    universalRoutes.includes(currentPath) ||
    currentPath.startsWith('/v2/pharmacy');

  if (isPharmacyTech && !pharmacyTechAllowed) {
    return <Navigate to="/" replace />;
  }

  if (currentPath === '/documents' && currentRole !== UserRoles.CUSTOMER_CARE) {
    return <Navigate to="/" replace />;
  }

  if (!getCookie('access_token')) {
    return <Navigate to="/auth/login" replace />;
  }

  // Temporary conditional to render new ui until all components are updated
  return !useNewUI ? (
    <>
      <NavBarIndex />
      {element}
    </>
  ) : (
    <NavBarIndex>{element}</NavBarIndex>
  );
};

const App = () => {
  const location = useLocation();
  // /status is public: render it outside the app shell so no session/monitor/MQTT bootstrap can 401-redirect it.
  if (location.pathname.replace(/\/+$/, '') === '/status') {
    return <StatusPage />;
  }
  return (
    <>
      <SessionManager />
      <DocumentProcessingMonitor />
      {/* Global so an incoming call rings on any page/UI, not just v2. */}
      <IncomingCallBannerV2 />
      <VideoCallModalV2 />
      <Routes>
        <Route path="/auth/login" element={<Login />} />
        <Route path="/auth/resetPassword" element={<ResetPassword />} />
        <Route path="/register/:token" element={<Register />} />
        <Route path="/register/verify" element={<RegisterEmailVerify />} />
        <Route
          path="/"
          element={<ProtectedRoute element={<DashboardIndex />} />}
        />
        <Route
          path="/v2/owners"
          element={<ProtectedRoute element={<HubOwners />} />}
        />
        <Route
          path="/v2/owners/:id"
          element={<ProtectedRoute element={<HubOwnerProfile />} />}
        />
        <Route
          path="/v2/caregivers"
          element={<ProtectedRoute element={<CaregiversNew />} />}
        />
        <Route
          path="/v2/caregivers/:id"
          element={<ProtectedRoute element={<CaregiverProfile />} />}
        />
        <Route
          path="/v2/hubs"
          element={<ProtectedRoute element={<HubIndex />} />}
        />
        <Route
          path="/v2/hubs/:id"
          element={<ProtectedRoute element={<HubProfile />} />}
        />
        <Route
          path="/v2/settings"
          element={<ProtectedRoute element={<AccountSettings />} />}
        />
        <Route
          path="/engineers"
          element={<ProtectedRoute element={<Engineers />} />}
        />
        <Route
          path="/v2/call-stats"
          element={
            <ProtectedRoute
              element={<EngineerOnlyRoute element={<CallStatsDashboard />} />}
            />
          }
        />
        <Route
          path="/customers"
          element={<ProtectedRoute element={<Customers />} />}
        />
        <Route
          path="/documents"
          element={<ProtectedRoute element={<DocumentsPage />} />}
        />
        <Route
          path="/customerCare"
          element={<ProtectedRoute element={<CustomerCareUsers />} />}
        />
        <Route
          path="/pharmacyTech"
          element={<ProtectedRoute element={<PharmacyTechUsers />} />}
        />
        <Route
          path="/customers/create"
          element={<ProtectedRoute element={<CreateNewFacilityUser />} />}
        />
        <Route
          path="/customers/owner/create"
          element={<ProtectedRoute element={<CreateNewOwner />} />}
        />
        <Route
          path="/customers/:customer_id"
          element={<ProtectedRoute element={<CustomerDetail />} />}
        />
        <Route
          path="/customers/:customer_id/medication/:med_event_id/history"
          element={<ProtectedRoute element={<MedicationEventHistory />} />}
        />
        <Route
          path="/customers/:customer_id/care-team/:care_team_id"
          element={<ProtectedRoute element={<CareTeamDetails />} />}
        />
        <Route
          path="/customers/:customer_id/create-primary-contact"
          element={<ProtectedRoute element={<CreatePrimaryContact />} />}
        />
        <Route
          path="/devices"
          element={<ProtectedRoute element={<Devices />} />}
        />
        <Route
          path="/device/:deviceSerialNumber"
          element={<ProtectedRoute element={<DeviceDetail />} />}
        />
        <Route
          path="/facility"
          element={<ProtectedRoute element={<MyFacilityDetail />} />}
        />
        <Route
          path="/facilities"
          element={<ProtectedRoute element={<Facilities />} />}
        />
        <Route
          path="/v2/facilities"
          element={<ProtectedRoute element={<FacilitiesOverview />} />}
        />
        <Route
          path="/v2/facilities/:facility_id"
          element={<ProtectedRoute element={<FacilityDetails />} />}
        />
        <Route
          path="/v2/facilities/:facility_id/location/:location_id"
          element={<ProtectedRoute element={<FacilityDetails />} />}
        />
        <Route
          path="/facilities/add"
          element={<ProtectedRoute element={<AddNewFacility />} />}
        />
        <Route
          path="/facilities/:facility_id"
          element={<ProtectedRoute element={<FacilityDetail />} />}
        />
        <Route
          path="/myAccount"
          element={<ProtectedRoute element={<MyAccount />} />}
        />
        <Route
          path="/owners"
          element={<ProtectedRoute element={<Owners />} />}
        />
        <Route
          path="/pharmacies"
          element={<ProtectedRoute element={<Pharmacies />} />}
        />
        <Route
          path="/pharmacies/:pharmacy_id"
          element={<ProtectedRoute element={<PharmacyDetail />} />}
        />
        <Route
          path="/reporting"
          element={<ProtectedRoute element={<Reporting />} />}
        />
        <Route
          path="/ops"
          element={<ProtectedRoute element={<Operations />} />}
        />
        <Route
          path="/software"
          element={<ProtectedRoute element={<Software />} />}
        />
        <Route
          path="/v2/software"
          element={<ProtectedRoute element={<CustomerCareSoftwareView />} />}
        />
        <Route
          path="/softwareDeployment"
          element={<ProtectedRoute element={<SoftwareDeployment />} />}
        />
        <Route
          path="/softwareVersion/create"
          element={<ProtectedRoute element={<CreateNewSoftwareVersion />} />}
        />
        <Route
          path="/softwareVersion/:versionId"
          element={<ProtectedRoute element={<EditSoftwareVersion />} />}
        />
        <Route
          path="/v2/adherence"
          element={<ProtectedRoute element={<Adherence />} />}
        />
        <Route
          path="/v2/patients"
          element={<ProtectedRoute element={<PatientOverview />} />}
        />
        <Route
          path="/v2/patients/:id"
          element={<ProtectedRoute element={<PatientDetails />} />}
        />
        <Route
          path="/v2/pharmacy/patients"
          element={<ProtectedRoute element={<PharmacyPatientOverview />} />}
        />
        <Route
          path="/v2/pharmacy/patients/:id"
          element={<ProtectedRoute element={<PharmacyPatientDetails />} />}
        />
        <Route
          path="/v2/patient-survey"
          element={<ProtectedRoute element={<PatientSurvey />} />}
        />
        <Route
          path="/v2/issues"
          element={<ProtectedRoute element={<Issues />} />}
        />
        {process.env.NODE_ENV === 'development' && (
          <Route
            path="/dev/janus-spike"
            element={<ProtectedRoute element={<JanusSpike />} />}
          />
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastContainer
        position="bottom-left"
        autoClose={10000}
        hideProgressBar={true}
        closeOnClick
        transition={Slide}
        theme="colored"
      />
    </>
  );
};

export default App;
